import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarDays, CalendarClock, User, Music4, LogOut, Menu, Plus, Wallet, Trophy, Shield, Library, UserPlus, Radio, Crown, CalendarSearch, BarChart3, Sun, Moon, Monitor, Rocket, Check, MessageSquare, Compass, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme, useBranding } from "@/context/ThemeContext";
import { APP_NAME, api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";
import Onboarding from "@/components/Onboarding";
import ExpressInvites from "@/components/express/ExpressInvites";

function HideOnExpressPages({ children }) {
  const { pathname } = useLocation();
  return /^\/em-cima-da-hora\/(oferta|servico)\//.test(pathname) ? null : children;
}

const NAV_CREATOR = [
  { to: "/", label: "Início", icon: LayoutDashboard, end: true },
  { to: "/eventos", label: "Eventos", icon: CalendarDays },
  { to: "/minha-equipe", label: "Minha Equipe", icon: Heart, feature: "contractor_basic" },
  { to: "/mensagens", label: "Mensagens", icon: MessageSquare },
  { to: "/agenda", label: "Minha Agenda", icon: CalendarClock, feature: "minha_agenda" },
  { to: "/repertorio", label: "Meu Repertório", icon: Library },
  { to: "/pagamentos", label: "Financeiro", icon: Wallet },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3 },
  { to: "/contratante-avancado", label: "Contratante Avançado", icon: Rocket, feature: "contractor_advanced" },
  { to: "/convidar", label: "Convidar", icon: UserPlus },
  { to: "/perfil", label: "Meu Perfil", icon: User },
  { to: "/meu-plano", label: "Meu Plano", icon: Crown },
];

const NAV_FREELANCER = [
  { to: "/", label: "Início", icon: LayoutDashboard, end: true },
  { to: "/buscar-eventos", label: "Oportunidades", icon: CalendarSearch },
  { to: "/eventos", label: "Eventos", icon: CalendarDays },
  { to: "/mensagens", label: "Mensagens", icon: MessageSquare },
  { to: "/agenda", label: "Minha Agenda", icon: CalendarClock, feature: "minha_agenda" },
  { to: "/repertorio", label: "Meu Repertório", icon: Library },
  { to: "/plantao", label: "Modo Plantão", icon: Radio, feature: "oncall" },
  { to: "/pagamentos", label: "Recebimentos", icon: Wallet },
  { to: "/ranking", label: "Ranking", icon: Trophy },
  { to: "/perfil", label: "Meu Perfil", icon: User },
  { to: "/meu-plano", label: "Meu Plano", icon: Crown },
];

const BOTTOM_CREATOR = [
  { to: "/", label: "Início", slug: "painel", icon: LayoutDashboard, end: true },
  { to: "/eventos", label: "Eventos", slug: "eventos", icon: CalendarDays },
  { to: "/mensagens", label: "Mensagens", slug: "mensagens", icon: MessageSquare },
  { to: "/pagamentos", label: "Financeiro", slug: "pagamentos", icon: Wallet },
  { to: "/perfil", label: "Perfil", slug: "perfil", icon: User },
];

const BOTTOM_FREELANCER = [
  { to: "/", label: "Início", slug: "painel", icon: LayoutDashboard, end: true },
  { to: "/buscar-eventos", label: "Oportunidades", slug: "oportunidades", icon: CalendarSearch },
  { to: "/agenda", label: "Agenda", slug: "agenda", icon: CalendarClock },
  { to: "/mensagens", label: "Mensagens", slug: "mensagens", icon: MessageSquare },
  { to: "/perfil", label: "Perfil", slug: "perfil", icon: User },
];

const NAV_BOTH = [
  { to: "/", label: "Início", icon: LayoutDashboard, end: true },
  { to: "/eventos", label: "Eventos", icon: CalendarDays },
  { to: "/buscar-eventos", label: "Oportunidades", icon: CalendarSearch },
  { to: "/minha-equipe", label: "Minha Equipe", icon: Heart, feature: "contractor_basic" },
  { to: "/mensagens", label: "Mensagens", icon: MessageSquare },
  { to: "/agenda", label: "Minha Agenda", icon: CalendarClock, feature: "minha_agenda" },
  { to: "/repertorio", label: "Meu Repertório", icon: Library },
  { to: "/pagamentos", label: "Financeiro", icon: Wallet },
  { to: "/plantao", label: "Modo Plantão", icon: Radio, feature: "oncall" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3 },
  { to: "/ranking", label: "Ranking", icon: Trophy },
  { to: "/contratante-avancado", label: "Contratante Avançado", icon: Rocket, feature: "contractor_advanced" },
  { to: "/convidar", label: "Convidar", icon: UserPlus },
  { to: "/perfil", label: "Meu Perfil", icon: User },
  { to: "/meu-plano", label: "Meu Plano", icon: Crown },
];

const BOTTOM_BOTH = [
  { to: "/", label: "Início", slug: "painel", icon: LayoutDashboard, end: true },
  { to: "/eventos", label: "Eventos", slug: "eventos", icon: CalendarDays },
  { to: "/buscar-eventos", label: "Oportun.", slug: "oportunidades", icon: CalendarSearch },
  { to: "/mensagens", label: "Mensagens", slug: "mensagens", icon: MessageSquare },
  { to: "/perfil", label: "Perfil", slug: "perfil", icon: User },
];

function NavItems({ onClick, isAdmin, hasFeature, nav }) {
  const base = nav.filter((n) => !n.feature || hasFeature(n.feature));
  const items = isAdmin ? [...base, { to: "/admin", label: "Admin", icon: Shield }] : base;
  return (
    <nav className="flex flex-col gap-1">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onClick}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"}`}>
          <n.icon className="h-[18px] w-[18px]" />{n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout, hasFeature } = useAuth();
  const pt = user?.user_type;
  const persona = pt === "creator" ? "creator" : pt === "both" ? "both" : "freelancer";
  const nav = persona === "creator" ? NAV_CREATOR : persona === "both" ? NAV_BOTH : NAV_FREELANCER;
  const bottomNav = persona === "creator" ? BOTTOM_CREATOR : persona === "both" ? BOTTOM_BOTH : BOTTOM_FREELANCER;
  const { theme, mode, setMode } = useTheme();
  const { branding } = useBranding();
  const logoSrc = (branding && (theme === "light" ? branding.logo_light : branding.logo_dark)) || (branding && branding.logo_main) || "/michamou-icon.png";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/chats/unread").then((r) => { if (alive) setChatUnread(r.data.count || 0); }).catch(() => {});
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const Brand = () => (
    <div className="flex items-center gap-2 px-2 py-1">
      <div className="h-9 w-9 rounded-sm overflow-hidden flex items-center justify-center bg-[#1A1A1F]">
        <img src={logoSrc} alt="Mí Chamou" className="h-full w-full object-cover" />
      </div>
      <div>
        <div className="font-head font-extrabold text-lg leading-none tracking-tight">{APP_NAME}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Músicos Freelancers</div>
      </div>
    </div>
  );

  const Sidebar = ({ onNav, logoutTestId = "logout-btn" }) => (
    <div className="flex flex-col h-full">
      <div className="mb-4 shrink-0"><Brand /></div>
      <div className="flex-1 overflow-y-auto min-h-0 -mr-2 pr-2">
        <NavItems onClick={onNav} nav={nav} hasFeature={hasFeature} isAdmin={["suporte", "financeiro", "admin", "super_admin"].includes(user?.role)} />
        {persona !== "freelancer" ? (
          <button data-testid="create-event-nav-btn" onClick={() => { navigate("/eventos/novo"); onNav?.(); }}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 text-sm font-semibold transition-colors">
            <Plus className="h-4 w-4" /> Criar evento
          </button>
        ) : (
          <button data-testid="find-events-nav-btn" onClick={() => { navigate("/buscar-eventos"); onNav?.(); }}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 text-sm font-semibold transition-colors">
            <Compass className="h-4 w-4" /> Encontrar eventos
          </button>
        )}
      </div>
      <div className="shrink-0 pt-3 mt-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="h-9 w-9 rounded-full bg-[#1C1C24] overflow-hidden flex items-center justify-center text-sm font-bold">
            {user?.photo ? <img src={user.photo} alt="" className="h-full w-full object-cover" /> : (user?.stage_name?.[0] || "?")}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.stage_name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
        </div>
        <button data-testid={logoutTestId} onClick={logout}
          className="flex items-center gap-3 px-4 py-2.5 w-full rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors">
          <LogOut className="h-[18px] w-[18px]" /> Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background overflow-x-hidden">
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:text-sm focus:font-semibold">Pular para o conteúdo</a>
      <aside className="hidden lg:flex w-64 flex-col border-r border-border p-4 fixed h-screen bg-surface">
        <Sidebar />
      </aside>
      <div className="flex-1 min-w-0 lg:ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8 h-16 border-b border-border bg-background/80 backdrop-blur-xl">
          <div className="lg:hidden"><Brand /></div>
          <div className="hidden lg:block font-head font-semibold text-muted-foreground">Olá, {user?.stage_name} 🎸</div>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="theme-toggle-btn" title="Tema"
                  aria-label="Alternar tema"
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors">
                  {theme === "light" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {[
                  { key: "light", label: "Claro", icon: Sun },
                  { key: "dark", label: "Escuro", icon: Moon },
                  { key: "system", label: "Seguir sistema", icon: Monitor },
                ].map((opt) => (
                  <DropdownMenuItem key={opt.key} data-testid={`theme-opt-${opt.key}`}
                    onClick={() => setMode(opt.key)} className="gap-2 cursor-pointer">
                    <opt.icon className="h-4 w-4" />
                    <span className="flex-1">{opt.label}</span>
                    {mode === opt.key && <Check className="h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <NotificationBell />
          </div>
        </header>
        <main id="conteudo" tabIndex={-1} className="flex-1 min-w-0 p-4 lg:p-8 pb-24 lg:pb-8 focus:outline-none"><HideOnExpressPages><ExpressInvites /></HideOnExpressPages>{children}</main>
      </div>

      {/* Bottom navigation (mobile/tablet) */}
      <nav data-testid="bottom-nav" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-surface/95 backdrop-blur-xl border-t border-white/10 flex items-stretch justify-around pb-[env(safe-area-inset-bottom)]">
        {bottomNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottomnav-${n.slug}`}
            className={({ isActive }) => `group relative flex flex-col items-center justify-center gap-1 flex-1 min-w-0 min-h-[56px] py-2 text-[10px] font-semibold transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
            {({ isActive }) => (
              <>
                <span aria-hidden="true" className={`absolute top-0 h-[3px] w-9 rounded-full transition-all ${isActive ? "bg-primary shadow-[0_0_12px_hsl(var(--primary)/0.8)]" : "bg-transparent"}`} />
                <span className="relative">
                  <n.icon className={`h-[22px] w-[22px] transition-transform ${isActive ? "scale-110" : "group-active:scale-95"}`} />
                  {n.slug === "mensagens" && chatUnread > 0 && (
                    <span data-testid="bottomnav-mensagens-badge" className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#FF4D00] text-white text-[9px] font-bold flex items-center justify-center">{chatUnread > 9 ? "9+" : chatUnread}</span>
                  )}
                </span>
                <span className="truncate max-w-full px-1">{n.label}</span>
              </>
            )}
          </NavLink>
        ))}
        <button data-testid="bottomnav-mais" aria-label="Abrir menu completo" onClick={() => setOpen(true)}
          className="group relative flex flex-col items-center justify-center gap-1 flex-1 min-h-[56px] py-2 text-[10px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
          <Menu className="h-[22px] w-[22px] group-active:scale-95 transition-transform" /><span>Mais</span>
        </button>
      </nav>

      {/* Full menu drawer (controlled) — contém todos os itens + Sair */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-surface border-border w-72 p-4 flex flex-col overflow-y-auto lg:hidden">
          <SheetHeader className="sr-only"><SheetTitle>Menu</SheetTitle></SheetHeader>
          <Sidebar onNav={() => setOpen(false)} logoutTestId="logout-btn-mobile" />
        </SheetContent>
      </Sheet>

      <Onboarding />
    </div>
  );
}
